'use client';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { PopularBrands } from '@/app/[locale]/(browse)/components/PopularBrands';
import {
  type BubbleItem,
  BubbleNavigation,
} from '@/app/[locale]/(browse)/explore/components/BubbleNavigation';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type CMSExplorePageBubbleNavItem } from '@/modules/cms/types';
import { transformBubbleNavItems } from '@/modules/explore/helpers';

const cloudfrontHost = process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST;

type QuizResultsHeaderProps = {
  slug: string;
  description: string;
  bubbleNavItems?: CMSExplorePageBubbleNavItem[];
  popularBrandSlugs?: string[];
};

export function QuizResultsHeader({
  slug,
  description,
  bubbleNavItems,
  popularBrandSlugs,
}: QuizResultsHeaderProps) {
  const t = useTranslations('quiz');
  const { sendActivityTrackerEvent } = useActivityTracker();

  const moduleName = `explore_${slug}_bubbles`;

  function handleBubbleClick(item: BubbleItem, index: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName,
      itemClicked: item.label,
      itemPosition: index,
      schemaVersion: '3.0',
    });
  }

  const endOfStyleName = description.indexOf('.');
  const styleName = description.slice(0, endOfStyleName + 1);
  const descriptionWithoutStyleName = description.slice(endOfStyleName + 1);

  return (
    <div className={styles.headerWrapper}>
      <div className={styles.headerContentContainer}>
        <div className={styles.headerContent}>
          <Heading variant="xl">{t('QuizResultsHeader.Title')}</Heading>
          <Body variant="sm-regular" className={styles.description}>
            <strong>{styleName}</strong>
            {descriptionWithoutStyleName}
          </Body>
        </div>
        <img
          className={styles.image}
          src={`${cloudfrontHost}/web/assets/quiz/find-your-style/results/${slug}.png`}
          alt=""
        />
      </div>
      {bubbleNavItems && bubbleNavItems.length > 0 && (
        <div className={styles.bubbleNavigationWrapper}>
          <BubbleNavigation
            items={transformBubbleNavItems(bubbleNavItems, moduleName)}
            onBubbleClick={handleBubbleClick}
          />
        </div>
      )}
      {popularBrandSlugs && popularBrandSlugs.length > 0 && (
        <div className={styles.popularBrandsWrapper}>
          <PopularBrands allowedBrandSlugs={popularBrandSlugs} />
        </div>
      )}
    </div>
  );
}
